import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import {
  FaCheckCircle,
  FaChartBar,
  FaCog,
  FaBolt,
  FaKey,
  FaComments,
  FaQuestionCircle,
  FaThumbsUp,
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSync
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { EDUGENIE_AVATAR_SRC } from '../../../components/edugenie/edugenieBrand';

const TABS = [
  { id: 'overview', label: 'Overview', icon: FaChartBar },
  { id: 'analytics', label: 'Analytics', icon: FaChartBar },
  { id: 'intents', label: 'Intents', icon: FaBolt },
  { id: 'keywords', label: 'Keywords', icon: FaKey },
  { id: 'unknown', label: 'Unknown Qs', icon: FaQuestionCircle },
  { id: 'conversations', label: 'Logs', icon: FaComments },
  { id: 'feedback', label: 'Feedback', icon: FaThumbsUp },
  { id: 'settings', label: 'Settings', icon: FaCog }
];

const PANEL_SETTING_PREFIXES = [
  'bot_tagline_',
  'greeting_text_',
  'suggested_questions_'
];

const PANEL_LABELS = {
  user: 'Learner',
  org: 'Organization',
  instructor: 'Instructor',
  admin: 'Admin'
};

const emptyIntentForm = {
  intent_code: '',
  intent_name: '',
  service_key: '',
  description: '',
  confidence_threshold: 0.4,
  is_enabled: true,
  sort_order: 0
};

const emptyKeywordForm = {
  intent_id: '',
  keyword: '',
  weight: 1,
  is_enabled: true
};

const StatCard = ({ label, value, accent = 'text-gray-900' }) => (
  <div className="rounded-xl sm:rounded-2xl shadow-md bg-white border border-purple-100 h-100 p-3 sm:p-4">
    <div className="text-xs sm:text-sm text-purple-600 font-semibold uppercase tracking-wide">{label}</div>
    <div className={`text-2xl sm:text-3xl font-extrabold mt-1 ${accent}`}>{value ?? 0}</div>
  </div>
);

const SectionCard = ({ title, actions = null, children }) => (
  <div className="overflow-hidden rounded-xl sm:rounded-2xl shadow-lg bg-white border border-purple-100 mb-4">
    <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-3 sm:px-4 py-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
      <h3 className="text-white fw-bold text-sm sm:text-base mb-0">{title}</h3>
      {actions}
    </div>
    <div className="p-3 sm:p-4">{children}</div>
  </div>
);

const EduGenieAdmin = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [intents, setIntents] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [settings, setSettings] = useState([]);
  const [unknowns, setUnknowns] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [feedback, setFeedback] = useState([]);

  const [intentSearch, setIntentSearch] = useState('');
  const [keywordSearch, setKeywordSearch] = useState('');
  const [logSearch, setLogSearch] = useState('');
  const [unknownStatus, setUnknownStatus] = useState('PENDING');
  const [settingsFilter, setSettingsFilter] = useState('all');

  const [showIntentModal, setShowIntentModal] = useState(false);
  const [editingIntent, setEditingIntent] = useState(null);
  const [intentForm, setIntentForm] = useState(emptyIntentForm);

  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState(null);
  const [keywordForm, setKeywordForm] = useState(emptyKeywordForm);

  const [answerModal, setAnswerModal] = useState(null);
  const [adminAnswer, setAdminAnswer] = useState('');
  const [convertToFaq, setConvertToFaq] = useState(true);

  const [testMessage, setTestMessage] = useState('Resume my course');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleLoadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.edugenie.getAdminStatus();
      if (response.data?.success) {
        setStatus(response.data.data);
        setAnalytics(response.data.data.analytics || null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLoadIntents = useCallback(async () => {
    try {
      const response = await api.edugenie.listIntents({ search: intentSearch });
      if (response.data?.success) setIntents(response.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  }, [intentSearch]);

  const handleLoadKeywords = useCallback(async () => {
    try {
      const response = await api.edugenie.listKeywords({ search: keywordSearch });
      if (response.data?.success) setKeywords(response.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  }, [keywordSearch]);

  const handleLoadSettings = useCallback(async () => {
    try {
      const response = await api.edugenie.listSettings();
      if (response.data?.success) setSettings(response.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  }, []);

  const handleLoadUnknowns = useCallback(async () => {
    try {
      const response = await api.edugenie.listUnknownQuestions({ status: unknownStatus, limit: 50 });
      if (response.data?.success) setUnknowns(response.data.data?.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  }, [unknownStatus]);

  const handleLoadConversations = useCallback(async () => {
    try {
      const response = await api.edugenie.listConversations({ search: logSearch, limit: 30 });
      if (response.data?.success) setConversations(response.data.data?.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  }, [logSearch]);

  const handleLoadFeedback = useCallback(async () => {
    try {
      const response = await api.edugenie.listFeedback({ limit: 30 });
      if (response.data?.success) setFeedback(response.data.data?.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  }, []);

  const handleLoadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res = await api.edugenie.getAnalytics();
      if (res.data?.success) setAnalytics(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  useEffect(() => {
    handleLoadOverview();
  }, [handleLoadOverview]);

  useEffect(() => {
    if (activeTab === 'intents' || activeTab === 'keywords') handleLoadIntents();
    if (activeTab === 'keywords') handleLoadKeywords();
    if (activeTab === 'settings') handleLoadSettings();
    if (activeTab === 'unknown') handleLoadUnknowns();
    if (activeTab === 'conversations') handleLoadConversations();
    if (activeTab === 'feedback') handleLoadFeedback();
    if (activeTab === 'analytics') handleLoadAnalytics();
  }, [
    activeTab,
    handleLoadIntents,
    handleLoadKeywords,
    handleLoadSettings,
    handleLoadUnknowns,
    handleLoadConversations,
    handleLoadFeedback,
    handleLoadAnalytics
  ]);

  const counts = status?.counts || {};

  const isPanelSetting = (key = '') =>
    PANEL_SETTING_PREFIXES.some((prefix) => key.startsWith(prefix));

  const filteredSettings = useMemo(() => {
    if (settingsFilter === 'global') {
      return settings.filter((s) => !isPanelSetting(s.setting_key));
    }
    if (settingsFilter === 'panel') {
      return settings.filter((s) => isPanelSetting(s.setting_key));
    }
    return settings;
  }, [settings, settingsFilter]);

  const handleTestIntent = async (event) => {
    event.preventDefault();
    setTesting(true);
    setTestResult(null);
    try {
      const response = await api.edugenie.testIntent({ message: testMessage.trim() });
      if (response.data?.success) {
        setTestResult(response.data.data);
        toast.success(response.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setTesting(false);
    }
  };

  const handleOpenIntentModal = (intent = null) => {
    if (intent) {
      setEditingIntent(intent);
      setIntentForm({
        intent_code: intent.intent_code,
        intent_name: intent.intent_name,
        service_key: intent.service_key,
        description: intent.description || '',
        confidence_threshold: Number(intent.confidence_threshold) || 0.4,
        is_enabled: !!intent.is_enabled,
        sort_order: intent.sort_order || 0
      });
    } else {
      setEditingIntent(null);
      setIntentForm(emptyIntentForm);
    }
    setShowIntentModal(true);
  };

  const handleSaveIntent = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (editingIntent) {
        await api.edugenie.updateIntent(editingIntent.id, intentForm);
        toast.success('Intent updated');
      } else {
        await api.edugenie.createIntent(intentForm);
        toast.success('Intent created');
      }
      setShowIntentModal(false);
      handleLoadIntents();
      handleLoadOverview();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteIntent = async (intent) => {
    if (!window.confirm(`Delete intent "${intent.intent_name}"?`)) return;
    try {
      await api.edugenie.deleteIntent(intent.id);
      toast.success('Intent deleted');
      handleLoadIntents();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  const handleOpenKeywordModal = (keyword = null) => {
    if (keyword) {
      setEditingKeyword(keyword);
      setKeywordForm({
        intent_id: keyword.intent_id,
        keyword: keyword.keyword,
        weight: Number(keyword.weight) || 1,
        is_enabled: !!keyword.is_enabled
      });
    } else {
      setEditingKeyword(null);
      setKeywordForm({
        ...emptyKeywordForm,
        intent_id: intents[0]?.id || ''
      });
    }
    setShowKeywordModal(true);
  };

  const handleSaveKeyword = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (editingKeyword) {
        await api.edugenie.updateKeyword(editingKeyword.id, keywordForm);
        toast.success('Keyword updated');
      } else {
        await api.edugenie.createKeyword(keywordForm);
        toast.success('Keyword created');
      }
      setShowKeywordModal(false);
      handleLoadKeywords();
      handleLoadOverview();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKeyword = async (keyword) => {
    if (!window.confirm(`Delete keyword "${keyword.keyword}"?`)) return;
    try {
      await api.edugenie.deleteKeyword(keyword.id);
      toast.success('Keyword deleted');
      handleLoadKeywords();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  const handleSaveSetting = async (setting) => {
    try {
      await api.edugenie.upsertSetting({
        setting_key: setting.setting_key,
        setting_value: setting.setting_value,
        description: setting.description
      });
      toast.success('Setting saved');
      handleLoadSettings();
      handleLoadOverview();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  const handleAnswerUnknown = async () => {
    if (!answerModal || !adminAnswer.trim()) {
      toast.error('Answer is required');
      return;
    }
    setSaving(true);
    try {
      await api.edugenie.answerUnknownQuestion(answerModal.id, {
        admin_answer: adminAnswer.trim(),
        convert_to_faq: convertToFaq
      });
      toast.success(convertToFaq ? 'Converted to FAQ' : 'Answer saved');
      setAnswerModal(null);
      setAdminAnswer('');
      handleLoadUnknowns();
      handleLoadOverview();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleIgnoreUnknown = async (row) => {
    try {
      await api.edugenie.ignoreUnknownQuestion(row.id);
      toast.success('Ignored');
      handleLoadUnknowns();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  const intentOptions = useMemo(
    () => intents.map((i) => ({ value: i.id, label: `${i.intent_name} (${i.intent_code})` })),
    [intents]
  );

  const settingPanelBadge = (key = '') => {
    const match = key.match(/_(user|org|instructor|admin)$/);
    if (!match) return null;
    return PANEL_LABELS[match[1]] || match[1];
  };

  if (loading && !status) {
    return (
      <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen flex items-center justify-center">
        <Spinner animation="border" className="text-purple-600" role="status" aria-label="Loading EduGenie admin" />
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      {/* Header */}
      <div className="mb-4 d-flex flex-wrap align-items-center gap-3 justify-content-between">
        <div className="d-flex align-items-center gap-3 min-w-0">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden shadow-md border-2 border-white bg-purple-100 flex-shrink-0">
            <img
              src={EDUGENIE_AVATAR_SRC}
              alt="EduGenie"
              className="w-full h-full object-cover object-top"
              draggable={false}
            />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 m-0 leading-tight">
              EduGenie Admin
            </h1>
            <p className="text-slate-600 mb-0 text-sm truncate">
              Configure intents, keywords, and panel assistants
            </p>
          </div>
        </div>
        {status?.is_enabled ? (
          <Badge bg="success" className="d-inline-flex align-items-center gap-1 px-3 py-2 rounded-pill">
            <FaCheckCircle aria-hidden="true" /> Enabled · v{status?.version || '9.0.0'}
          </Badge>
        ) : (
          <Badge bg="secondary" className="px-3 py-2 rounded-pill">Disabled</Badge>
        )}
      </div>

      {/* Tabs */}
      <div
        className="d-flex flex-nowrap gap-2 mb-4 overflow-x-auto pb-1"
        role="tablist"
        aria-label="EduGenie admin sections"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              tabIndex={0}
              aria-label={tab.label}
              className={`flex-shrink-0 d-inline-flex align-items-center gap-2 px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold border transition-all ${
                active
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border-transparent shadow-md'
                  : 'bg-white text-purple-700 border-purple-200 hover:border-purple-400 hover:bg-purple-50'
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon aria-hidden="true" />
              <span className="whitespace-nowrap">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Overview */}
      {activeTab === 'overview' ? (
        <>
          <Row className="g-3 mb-4">
            {[
              ['Intents', counts.intents],
              ['Keywords', counts.keywords],
              ['Conversations', counts.conversations],
              ['Unknown', counts.pending_unknown_questions, 'text-amber-600'],
              ['Feedback', counts.feedback_submitted],
              ['Quick Actions', counts.quick_actions]
            ].map(([label, value, accent]) => (
              <Col key={label} xs={6} md={4} lg={2}>
                <StatCard label={label} value={value} accent={accent} />
              </Col>
            ))}
          </Row>

          <SectionCard title="Test Intent Detection">
            <Form onSubmit={handleTestIntent}>
              <Row className="g-2 align-items-end">
                <Col xs={12} md={9}>
                  <Form.Label className="small text-purple-700 fw-semibold">Sample message</Form.Label>
                  <Form.Control
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    aria-label="Test message"
                    className="rounded-xl border-purple-200"
                    placeholder="Type a learner or admin question…"
                  />
                </Col>
                <Col xs={12} md={3}>
                  <Button
                    type="submit"
                    disabled={testing || !testMessage.trim()}
                    className="w-100 rounded-xl border-0 bg-gradient-to-r from-purple-600 to-pink-600 fw-semibold py-2"
                  >
                    {testing ? 'Testing…' : 'Test'}
                  </Button>
                </Col>
              </Row>
            </Form>
            {testResult ? (
              <div className="mt-3 p-3 rounded-xl bg-purple-50 border border-purple-100 d-flex flex-wrap gap-2 align-items-center">
                <Badge bg={testResult.matched ? 'success' : 'warning'} text={testResult.matched ? undefined : 'dark'}>
                  {testResult.matched ? testResult.intent?.intent_code : 'No match'}
                </Badge>
                <Badge bg="secondary">
                  {(Number(testResult.confidence || 0) * 100).toFixed(1)}% confidence
                </Badge>
                {testResult.intent?.service_key ? (
                  <code className="small text-purple-800">{testResult.intent.service_key}</code>
                ) : null}
              </div>
            ) : null}
          </SectionCard>

          <Alert className="rounded-xl border border-purple-100 bg-white/80 text-purple-800 mb-0">
            Panel assistants (Learner, Org, Instructor, Admin) use menu-aligned greetings and quick actions.
            Override copy in <strong>Settings</strong> with keys like <code>bot_tagline_user</code>.
          </Alert>
        </>
      ) : null}

      {/* Analytics */}
      {activeTab === 'analytics' ? (
        analyticsLoading && !analytics ? (
          <div className="text-center py-16">
            <Spinner animation="border" className="text-purple-600" />
          </div>
        ) : analytics ? (
          <Row className="g-3">
            <Col xs={6} md={3}><StatCard label="Total Chats" value={analytics.total_chats} /></Col>
            <Col xs={6} md={3}><StatCard label="Successful" value={analytics.successful_responses} accent="text-green-600" /></Col>
            <Col xs={6} md={3}><StatCard label="Failed / Unknown" value={analytics.failed_responses} accent="text-red-600" /></Col>
            <Col xs={6} md={3}><StatCard label="Avg Response" value={`${analytics.average_response_ms || 0} ms`} /></Col>

            <Col md={6}>
              <SectionCard title="Popular Intents">
                <div className="overflow-x-auto">
                  <Table hover size="sm" className="mb-0 align-middle">
                    <tbody>
                      {(analytics.popular_intents || []).length ? (
                        (analytics.popular_intents || []).map((row) => (
                          <tr key={row.intent_code}>
                            <td><code className="text-purple-700">{row.intent_code}</code></td>
                            <td className="text-end fw-bold">{row.count}</td>
                          </tr>
                        ))
                      ) : (
                        <tr><td className="text-muted small">No intent data yet.</td></tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </SectionCard>
            </Col>
            <Col md={6}>
              <SectionCard
                title="Daily Chats (30d)"
                actions={(
                  <button
                    type="button"
                    className="border border-white/50 bg-white/20 text-white rounded-lg px-2 py-1 text-xs"
                    onClick={handleLoadAnalytics}
                    aria-label="Refresh analytics"
                  >
                    <FaSync />
                  </button>
                )}
              >
                <div className="overflow-x-auto">
                  <Table hover size="sm" className="mb-0 align-middle">
                    <tbody>
                      {(analytics.daily_chats || []).slice(-10).length ? (
                        (analytics.daily_chats || []).slice(-10).map((row) => (
                          <tr key={String(row.day)}>
                            <td className="small">{String(row.day)}</td>
                            <td className="text-end fw-bold">{row.count}</td>
                          </tr>
                        ))
                      ) : (
                        <tr><td className="text-muted small">No daily chat data yet.</td></tr>
                      )}
                    </tbody>
                  </Table>
                </div>
                <div className="mt-2 small text-muted">
                  Feedback ↑ {analytics.feedback?.up || 0} · ↓ {analytics.feedback?.down || 0}
                </div>
              </SectionCard>
            </Col>
          </Row>
        ) : (
          <Alert className="rounded-xl bg-white border border-purple-100 text-purple-700">
            No analytics available yet.
          </Alert>
        )
      ) : null}

      {/* Intents */}
      {activeTab === 'intents' ? (
        <SectionCard
          title="Intents"
          actions={(
            <Button
              size="sm"
              className="border border-white/50 bg-white/20 text-white rounded-lg d-inline-flex align-items-center gap-1"
              onClick={() => handleOpenIntentModal()}
            >
              <FaPlus /> Add Intent
            </Button>
          )}
        >
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Form.Control
              style={{ maxWidth: 280 }}
              placeholder="Search intents"
              value={intentSearch}
              onChange={(e) => setIntentSearch(e.target.value)}
              aria-label="Search intents"
              className="rounded-xl border-purple-200"
            />
            <Button
              variant="outline-secondary"
              className="rounded-xl border-purple-300 text-purple-700"
              onClick={handleLoadIntents}
              aria-label="Search"
            >
              <FaSearch />
            </Button>
          </div>
          <div className="overflow-x-auto rounded-xl border border-purple-100">
            <Table hover className="mb-0 align-middle text-sm min-w-max">
              <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                <tr>
                  <th className="py-3 px-3">Code</th>
                  <th className="py-3 px-3">Name</th>
                  <th className="py-3 px-3">Service</th>
                  <th className="py-3 px-3">Keywords</th>
                  <th className="py-3 px-3">Threshold</th>
                  <th className="py-3 px-3">Status</th>
                  <th className="py-3 px-3 text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {intents.length ? intents.map((intent) => (
                  <tr key={intent.id} className="border-b border-purple-50 hover:bg-purple-50/40">
                    <td className="px-3 py-2"><code className="text-purple-700">{intent.intent_code}</code></td>
                    <td className="px-3 py-2 fw-medium">{intent.intent_name}</td>
                    <td className="px-3 py-2"><code className="small">{intent.service_key}</code></td>
                    <td className="px-3 py-2">{intent.keywords?.length || 0}</td>
                    <td className="px-3 py-2">{Number(intent.confidence_threshold)}</td>
                    <td className="px-3 py-2">
                      {intent.is_enabled
                        ? <Badge bg="success" className="rounded-pill">On</Badge>
                        : <Badge bg="secondary" className="rounded-pill">Off</Badge>}
                    </td>
                    <td className="px-3 py-2 text-end">
                      <div className="d-inline-flex gap-2">
                        <button
                          type="button"
                          className="border border-purple-300 text-purple-600 hover:bg-purple-100 rounded p-1.5"
                          onClick={() => handleOpenIntentModal(intent)}
                          aria-label={`Edit ${intent.intent_name}`}
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          className="border border-red-300 text-red-600 hover:bg-red-50 rounded p-1.5"
                          onClick={() => handleDeleteIntent(intent)}
                          aria-label={`Delete ${intent.intent_name}`}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={7} className="text-center text-muted py-4">No intents found.</td></tr>
                )}
              </tbody>
            </Table>
          </div>
        </SectionCard>
      ) : null}

      {/* Keywords */}
      {activeTab === 'keywords' ? (
        <SectionCard
          title="Keywords"
          actions={(
            <Button
              size="sm"
              className="border border-white/50 bg-white/20 text-white rounded-lg d-inline-flex align-items-center gap-1"
              onClick={() => handleOpenKeywordModal()}
            >
              <FaPlus /> Add Keyword
            </Button>
          )}
        >
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Form.Control
              style={{ maxWidth: 280 }}
              placeholder="Search keywords"
              value={keywordSearch}
              onChange={(e) => setKeywordSearch(e.target.value)}
              aria-label="Search keywords"
              className="rounded-xl border-purple-200"
            />
            <Button
              variant="outline-secondary"
              className="rounded-xl border-purple-300 text-purple-700"
              onClick={handleLoadKeywords}
              aria-label="Search keywords"
            >
              <FaSearch />
            </Button>
          </div>
          <div className="overflow-x-auto rounded-xl border border-purple-100">
            <Table hover className="mb-0 align-middle text-sm min-w-max">
              <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                <tr>
                  <th className="py-3 px-3">Keyword</th>
                  <th className="py-3 px-3">Intent</th>
                  <th className="py-3 px-3">Weight</th>
                  <th className="py-3 px-3">Status</th>
                  <th className="py-3 px-3 text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keywords.length ? keywords.map((kw) => (
                  <tr key={kw.id} className="border-b border-purple-50 hover:bg-purple-50/40">
                    <td className="px-3 py-2 fw-medium">{kw.keyword}</td>
                    <td className="px-3 py-2"><code className="text-purple-700">{kw.intent?.intent_code || kw.intent_id}</code></td>
                    <td className="px-3 py-2">{Number(kw.weight)}</td>
                    <td className="px-3 py-2">
                      {kw.is_enabled
                        ? <Badge bg="success" className="rounded-pill">On</Badge>
                        : <Badge bg="secondary" className="rounded-pill">Off</Badge>}
                    </td>
                    <td className="px-3 py-2 text-end">
                      <div className="d-inline-flex gap-2">
                        <button
                          type="button"
                          className="border border-purple-300 text-purple-600 hover:bg-purple-100 rounded p-1.5"
                          onClick={() => handleOpenKeywordModal(kw)}
                          aria-label={`Edit keyword ${kw.keyword}`}
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          className="border border-red-300 text-red-600 hover:bg-red-50 rounded p-1.5"
                          onClick={() => handleDeleteKeyword(kw)}
                          aria-label={`Delete keyword ${kw.keyword}`}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={5} className="text-center text-muted py-4">No keywords found.</td></tr>
                )}
              </tbody>
            </Table>
          </div>
        </SectionCard>
      ) : null}

      {/* Unknown */}
      {activeTab === 'unknown' ? (
        <SectionCard
          title="Unknown Questions"
          actions={(
            <Button
              size="sm"
              className="border border-white/50 bg-white/20 text-white rounded-lg"
              onClick={handleLoadUnknowns}
            >
              Refresh
            </Button>
          )}
        >
          <div className="d-flex gap-2 mb-3">
            <Form.Select
              style={{ maxWidth: 200 }}
              value={unknownStatus}
              onChange={(e) => setUnknownStatus(e.target.value)}
              aria-label="Filter unknown status"
              className="rounded-xl border-purple-200"
            >
              <option value="PENDING">Pending</option>
              <option value="ANSWERED">Answered</option>
              <option value="CONVERTED">Converted</option>
              <option value="IGNORED">Ignored</option>
              <option value="ALL">All</option>
            </Form.Select>
          </div>
          {!unknowns.length ? (
            <Alert className="rounded-xl border border-purple-100 bg-purple-50/50 text-purple-800 mb-0">
              No unknown questions.
            </Alert>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-purple-100">
              <Table hover className="mb-0 align-middle text-sm min-w-max">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="py-3 px-3">Question</th>
                    <th className="py-3 px-3">User</th>
                    <th className="py-3 px-3">Status</th>
                    <th className="py-3 px-3">Date</th>
                    <th className="py-3 px-3 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {unknowns.map((row) => (
                    <tr key={row.id} className="border-b border-purple-50">
                      <td className="px-3 py-2" style={{ maxWidth: 360 }}>{row.question}</td>
                      <td className="px-3 py-2">{row.user ? `${row.user.first_name} ${row.user.last_name}` : '—'}</td>
                      <td className="px-3 py-2"><Badge bg="warning" text="dark" className="rounded-pill">{row.status}</Badge></td>
                      <td className="px-3 py-2 small">{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                      <td className="px-3 py-2 text-end">
                        {row.status === 'PENDING' ? (
                          <div className="d-inline-flex gap-2">
                            <Button
                              size="sm"
                              className="rounded-lg border border-purple-300 bg-white text-purple-700"
                              onClick={() => {
                                setAnswerModal(row);
                                setAdminAnswer('');
                                setConvertToFaq(true);
                              }}
                            >
                              Answer
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              className="rounded-lg"
                              onClick={() => handleIgnoreUnknown(row)}
                            >
                              Ignore
                            </Button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </SectionCard>
      ) : null}

      {/* Conversations */}
      {activeTab === 'conversations' ? (
        <SectionCard title="Conversation Logs">
          <div className="d-flex gap-2 mb-3">
            <Form.Control
              style={{ maxWidth: 280 }}
              placeholder="Search logs"
              value={logSearch}
              onChange={(e) => setLogSearch(e.target.value)}
              aria-label="Search conversation logs"
              className="rounded-xl border-purple-200"
            />
            <Button
              variant="outline-secondary"
              className="rounded-xl border-purple-300 text-purple-700"
              onClick={handleLoadConversations}
              aria-label="Search logs"
            >
              <FaSearch />
            </Button>
          </div>
          <div className="overflow-x-auto rounded-xl border border-purple-100">
            <Table hover className="mb-0 align-middle text-sm min-w-max">
              <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                <tr>
                  <th className="py-3 px-3">User</th>
                  <th className="py-3 px-3">Question</th>
                  <th className="py-3 px-3">Intent</th>
                  <th className="py-3 px-3">OK</th>
                  <th className="py-3 px-3">ms</th>
                  <th className="py-3 px-3">When</th>
                </tr>
              </thead>
              <tbody>
                {conversations.length ? conversations.map((row) => (
                  <tr key={row.id} className="border-b border-purple-50">
                    <td className="px-3 py-2 small">{row.user?.email || row.user_id}</td>
                    <td className="px-3 py-2 small" style={{ maxWidth: 280 }}>{row.question}</td>
                    <td className="px-3 py-2"><code className="text-purple-700">{row.intent_code || '—'}</code></td>
                    <td className="px-3 py-2">{row.is_successful ? '✓' : '✗'}</td>
                    <td className="px-3 py-2">{row.response_ms ?? '—'}</td>
                    <td className="px-3 py-2 small">{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={6} className="text-center text-muted py-4">No conversation logs.</td></tr>
                )}
              </tbody>
            </Table>
          </div>
        </SectionCard>
      ) : null}

      {/* Feedback */}
      {activeTab === 'feedback' ? (
        <SectionCard title="User Feedback">
          <div className="overflow-x-auto rounded-xl border border-purple-100">
            <Table hover className="mb-0 align-middle text-sm min-w-max">
              <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                <tr>
                  <th className="py-3 px-3">Rating</th>
                  <th className="py-3 px-3">Question</th>
                  <th className="py-3 px-3">Comment</th>
                  <th className="py-3 px-3">User</th>
                  <th className="py-3 px-3">When</th>
                </tr>
              </thead>
              <tbody>
                {feedback.length ? feedback.map((row) => (
                  <tr key={row.id} className="border-b border-purple-50">
                    <td className="px-3 py-2">{row.feedback_rating === 1 ? '👍' : '👎'}</td>
                    <td className="px-3 py-2 small" style={{ maxWidth: 280 }}>{row.question}</td>
                    <td className="px-3 py-2 small">{row.feedback_comment || '—'}</td>
                    <td className="px-3 py-2 small">{row.user?.email || row.user_id}</td>
                    <td className="px-3 py-2 small">{row.feedback_at ? new Date(row.feedback_at).toLocaleString() : '—'}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={5} className="text-center text-muted py-4">No feedback yet.</td></tr>
                )}
              </tbody>
            </Table>
          </div>
        </SectionCard>
      ) : null}

      {/* Settings */}
      {activeTab === 'settings' ? (
        <SectionCard title="Settings">
          <div className="d-flex flex-wrap gap-2 mb-3">
            {[
              ['all', 'All'],
              ['global', 'Global'],
              ['panel', 'Per Panel']
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`px-3 py-1.5 rounded-pill text-xs font-semibold border ${
                  settingsFilter === id
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-white text-purple-700 border-purple-200'
                }`}
                onClick={() => setSettingsFilter(id)}
              >
                {label}
              </button>
            ))}
          </div>

          {!filteredSettings.length ? (
            <Alert className="rounded-xl border border-purple-100 bg-purple-50/50 text-purple-800 mb-0">
              No settings in this filter.
            </Alert>
          ) : (
            <div className="d-flex flex-column gap-3">
              {filteredSettings.map((setting) => {
                const panel = settingPanelBadge(setting.setting_key);
                const longValue = String(setting.setting_value || '').length > 80;
                return (
                  <div
                    key={setting.id}
                    className="p-3 rounded-xl border border-purple-100 bg-purple-50/30"
                  >
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                      <code className="fw-bold text-purple-800">{setting.setting_key}</code>
                      {panel ? (
                        <Badge bg="light" text="dark" className="border rounded-pill">{panel}</Badge>
                      ) : (
                        <Badge bg="secondary" className="rounded-pill">Global</Badge>
                      )}
                    </div>
                    {setting.description ? (
                      <div className="small text-muted mb-2">{setting.description}</div>
                    ) : null}
                    <Form.Control
                      as={longValue ? 'textarea' : 'input'}
                      rows={longValue ? 4 : undefined}
                      value={setting.setting_value}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSettings((prev) => prev.map((s) => (
                          s.id === setting.id ? { ...s, setting_value: value } : s
                        )));
                      }}
                      aria-label={`Setting ${setting.setting_key}`}
                      className="rounded-xl border-purple-200 bg-white"
                    />
                    <Button
                      size="sm"
                      className="mt-2 rounded-lg border border-purple-600 bg-white text-purple-700 fw-semibold"
                      onClick={() => handleSaveSetting(setting)}
                    >
                      Save
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>
      ) : null}

      {/* Intent Modal */}
      <Modal show={showIntentModal} onHide={() => setShowIntentModal(false)} centered>
        <Form onSubmit={handleSaveIntent}>
          <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0">
            <Modal.Title className="fs-5">{editingIntent ? 'Edit Intent' : 'Add Intent'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!editingIntent ? (
              <Form.Group className="mb-3">
                <Form.Label>Code</Form.Label>
                <Form.Control
                  required
                  value={intentForm.intent_code}
                  onChange={(e) => setIntentForm({ ...intentForm, intent_code: e.target.value })}
                  className="rounded-xl"
                />
              </Form.Group>
            ) : null}
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                value={intentForm.intent_name}
                onChange={(e) => setIntentForm({ ...intentForm, intent_name: e.target.value })}
                className="rounded-xl"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Service Key</Form.Label>
              <Form.Control
                required
                value={intentForm.service_key}
                onChange={(e) => setIntentForm({ ...intentForm, service_key: e.target.value })}
                className="rounded-xl"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confidence Threshold</Form.Label>
              <Form.Control
                type="number"
                step="0.05"
                min="0"
                max="1"
                value={intentForm.confidence_threshold}
                onChange={(e) => setIntentForm({ ...intentForm, confidence_threshold: e.target.value })}
                className="rounded-xl"
              />
            </Form.Group>
            <Form.Check
              type="switch"
              id="intent-enabled"
              label="Enabled"
              checked={!!intentForm.is_enabled}
              onChange={(e) => setIntentForm({ ...intentForm, is_enabled: e.target.checked })}
            />
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" className="rounded-lg" onClick={() => setShowIntentModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-lg border-0 bg-gradient-to-r from-purple-600 to-pink-600">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Keyword Modal */}
      <Modal show={showKeywordModal} onHide={() => setShowKeywordModal(false)} centered>
        <Form onSubmit={handleSaveKeyword}>
          <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0">
            <Modal.Title className="fs-5">{editingKeyword ? 'Edit Keyword' : 'Add Keyword'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Intent</Form.Label>
              <Form.Select
                required
                value={keywordForm.intent_id}
                onChange={(e) => setKeywordForm({ ...keywordForm, intent_id: e.target.value })}
                className="rounded-xl"
              >
                <option value="">Select intent</option>
                {intentOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Keyword</Form.Label>
              <Form.Control
                required
                value={keywordForm.keyword}
                onChange={(e) => setKeywordForm({ ...keywordForm, keyword: e.target.value })}
                className="rounded-xl"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Weight</Form.Label>
              <Form.Control
                type="number"
                step="0.1"
                min="0.1"
                value={keywordForm.weight}
                onChange={(e) => setKeywordForm({ ...keywordForm, weight: e.target.value })}
                className="rounded-xl"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" className="rounded-lg" onClick={() => setShowKeywordModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-lg border-0 bg-gradient-to-r from-purple-600 to-pink-600">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Answer Modal */}
      <Modal show={!!answerModal} onHide={() => setAnswerModal(null)} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0">
          <Modal.Title className="fs-5">Answer Unknown Question</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted bg-purple-50 border border-purple-100 rounded-xl p-3">{answerModal?.question}</p>
          <Form.Control
            as="textarea"
            rows={4}
            value={adminAnswer}
            onChange={(e) => setAdminAnswer(e.target.value)}
            placeholder="Admin answer"
            aria-label="Admin answer"
            className="rounded-xl"
          />
          <Form.Check
            className="mt-3"
            type="switch"
            id="convert-faq"
            label="Convert to FAQ automatically"
            checked={convertToFaq}
            onChange={(e) => setConvertToFaq(e.target.checked)}
          />
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" className="rounded-lg" onClick={() => setAnswerModal(null)}>Cancel</Button>
          <Button
            className="rounded-lg border-0 bg-gradient-to-r from-purple-600 to-pink-600"
            onClick={handleAnswerUnknown}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Answer'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EduGenieAdmin;
